import type {
  ApiSuccess,
  CreateTaskRequest,
  ListTasksQuery,
  TaskAttachment,
  TaskComment,
  TaskStatus,
  TaskSummary,
  UpdateTaskRequest,
} from '@todo/shared';
import { api, get, getPaged, patch, post } from './client';

export const taskApi = {
  /** GET /api/tasks */
  list(query: ListTasksQuery = {}): Promise<ApiSuccess<TaskSummary[]>> {
    return getPaged<TaskSummary[]>('/tasks', query as Record<string, unknown>);
  },

  byId(id: string): Promise<TaskSummary> {
    return get<TaskSummary>(`/tasks/${id}`);
  },

  create(body: CreateTaskRequest): Promise<TaskSummary> {
    return post<TaskSummary>('/tasks', body);
  },

  /** PUT /api/tasks/:id/assignees - replaces the whole set. */
  async setAssignees(
    id: string,
    assigneeIds: string[],
    leadId?: string,
  ): Promise<TaskSummary> {
    const response = await api.put<ApiSuccess<TaskSummary>>(
      `/tasks/${id}/assignees`,
      { assigneeIds, ...(leadId ? { leadId } : {}) },
    );
    return response.data.data;
  },

  /** PUT /api/tasks/:id/important - states the end state; idempotent. */
  async setImportant(id: string, important: boolean): Promise<TaskSummary> {
    const res = await api.put<ApiSuccess<TaskSummary>>(
      `/tasks/${id}/important`,
      { important },
    );
    return res.data.data;
  },

  /** GET /api/tasks/:id/comments - the thread, oldest first. */
  comments(id: string): Promise<TaskComment[]> {
    return get<TaskComment[]>(`/tasks/${id}/comments`);
  },

  /** POST /api/tasks/:id/comments - returns the refreshed thread. */
  addComment(id: string, body: string): Promise<TaskComment[]> {
    return post<TaskComment[]>(`/tasks/${id}/comments`, { body });
  },

  /** GET /api/tasks/:id/attachments - each row carries a short-lived
   *  downloadUrl; re-fetch rather than caching them. */
  attachments(id: string): Promise<TaskAttachment[]> {
    return get<TaskAttachment[]>(`/tasks/${id}/attachments`);
  },

  /**
   * POST /api/tasks/:id/attachments - the File goes straight into the body
   * (no FormData), its name in the query string. Returns the refreshed list.
   */
  async uploadAttachment(id: string, file: File): Promise<TaskAttachment[]> {
    const res = await api.post<ApiSuccess<TaskAttachment[]>>(
      `/tasks/${id}/attachments`,
      file,
      {
        params: { filename: file.name },
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      },
    );
    return res.data.data;
  },

  /** PATCH /api/tasks/:id - edit fields after creation (creator or admin). */
  update(id: string, changes: UpdateTaskRequest): Promise<TaskSummary> {
    return patch<TaskSummary>(`/tasks/${id}`, changes);
  },

  /**
   * DELETE /api/tasks/:id/attachments/:attachmentId - remove a wrong upload.
   * Returns the remaining files, same shape as upload, so the client just
   * swaps its list.
   */
  async deleteAttachment(id: string, attachmentId: string): Promise<TaskAttachment[]> {
    const res = await api.delete<ApiSuccess<TaskAttachment[]>>(
      `/tasks/${id}/attachments/${attachmentId}`,
    );
    return res.data.data;
  },

  async setStatus(
    id: string,
    status: TaskStatus,
    blockedReason?: string,
  ): Promise<TaskSummary> {
    const res = await api.patch<ApiSuccess<TaskSummary>>(`/tasks/${id}/status`, {
      status,
      ...(blockedReason ? { blockedReason } : {}),
      // A drag to On hold records WHY without a dialog: it was postponed.
      ...(status === 'CANCELLED' ? { cancelReason: 'POSTPONED' } : {}),
    });
    return res.data.data;
  },
};
